import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import {
  GetOneClickBettingResponse,
  OneClickBettingConfig,
  SaveOneClickBettingResponse,
  oneClickBettingApi,
} from '@/store/api/oneClickBettingApi';

type OneClickField = 'firstField' | 'secondField' | 'thirdField';

interface OneClickBetState {
  isOneClickOn: boolean;
  defaultAmount: string;
  config: OneClickBettingConfig;
}

const fallbackConfig = (): OneClickBettingConfig => ({
  id: '',
  userId: '',
  firstField: '0',
  secondField: '0',
  thirdField: '0',
});

const normalizeValue = (value: unknown) => String(value ?? '0');

const normalizeResponse = (
  payload?: GetOneClickBettingResponse | SaveOneClickBettingResponse
): OneClickBettingConfig => {
  const rawData = payload?.data && typeof payload.data === 'object' ? payload.data : payload;

  if (!rawData || typeof rawData !== 'object') {
    return fallbackConfig();
  }

  const data = rawData as Record<string, unknown>;

  return {
    id: String(data.id ?? data.oneClickId ?? '1'),
    userId: normalizeValue(data.userId ?? ''),
    firstField: normalizeValue(data.firstField),
    secondField: normalizeValue(data.secondField),
    thirdField: normalizeValue(data.thirdField),
  };
};

const initialState: OneClickBetState = {
  isOneClickOn: false,
  defaultAmount: '0',
  config: fallbackConfig(),
};

const oneClickBetSlice = createSlice({
  name: 'oneClickBet',
  initialState,
  reducers: {
    setIsOneClickOn: (state, action: PayloadAction<boolean>) => {
      state.isOneClickOn = action.payload;
    },
    setOneClickField: (
      state,
      action: PayloadAction<{ field: OneClickField; value: string }>
    ) => {
      const { field, value } = action.payload;
      const previousFirstField = state.config.firstField;
      state.config[field] = value;

      if (field === 'firstField' && state.defaultAmount === previousFirstField) {
        state.defaultAmount = value;
      }
    },
    setDefaultAmount: (state, action: PayloadAction<string>) => {
      state.defaultAmount = action.payload;
    },
    resetOneClickBet: () => initialState,
  },
  extraReducers: (builder) => {
    builder.addMatcher(
      oneClickBettingApi.endpoints.getOneClickBetting.matchFulfilled,
      (state, { payload }) => {
        state.config = normalizeResponse(payload);
        state.defaultAmount = state.config.firstField;
      }
    );

    builder.addMatcher(oneClickBettingApi.endpoints.getOneClickBetting.matchRejected, (state) => {
      state.config = fallbackConfig();
      state.defaultAmount = '0';
    });

    builder.addMatcher(
      oneClickBettingApi.endpoints.saveOneClickBetting.matchFulfilled,
      (state, { payload }) => {
        state.config = normalizeResponse(payload);
        state.defaultAmount = state.config.firstField;
      }
    );

    builder.addMatcher(
      oneClickBettingApi.endpoints.saveOneClickBetting.matchRejected,
      (state) => {
        state.config = fallbackConfig();
        state.defaultAmount = '0';
      }
    );
  },
});

export const { setIsOneClickOn, setOneClickField, setDefaultAmount, resetOneClickBet } =
  oneClickBetSlice.actions;

export default oneClickBetSlice.reducer;
